import { computed, onMounted, ref, watch } from "vue";
import http, { httpMultipart } from "@/services/http";
import { useAuthStore } from "@/store/auth";
import { useClientStore } from "@/store/clientStore";
import { useCachedResource } from "@/composables/useCachedResource";
import { CACHE_TTL, buildCacheKey } from "@/config/cache";
import type {
  DashboardAnalyticsResponse,
  DashboardClientOption,
  DashboardFilters,
  DashboardRole,
  DashboardReportItem,
  DashboardSummary,
  DashboardReportRequest,
} from "@/types/dashboard";

const DEFAULT_FILTERS: DashboardFilters = {
  client_id: "all",
  service_id: null,
  package_id: null,
  date_from: null,
  date_to: null,
};

function getClientDashboardStartDate(
  sourceDate?: string | null,
  createdAt?: string | null,
): string | null {
  const value = sourceDate || createdAt || null;
  if (!value) return null;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  return date.toISOString().slice(0, 10);
}

function normalizeSelectValue(value: string | null | undefined): string | null {
  if (value === null || value === undefined) return null;

  const trimmed = String(value).trim();
  if (!trimmed) return null;

  if (["all", "null", "undefined"].includes(trimmed.toLowerCase())) {
    return null;
  }

  return trimmed;
}

function toNumber(value: unknown, fallback = 0): number {
  const parsed = typeof value === "string" ? Number(value) : Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function createEmptySummary(): DashboardSummary {
  return {
    totalTickets: 0,
    notStartedTickets: 0,
    ongoingTickets: 0,
    closedTickets: 0,
    openTickets: 0,
    ticketRequests: 0,
    newLeads: 0,
    total_tasks: 0,
    in_progress: 0,
    completed: 0,
    pending: 0,
    overdue: 0,
    active_tickets: 0,
    ticket_requests: 0,
    new_leads: 0,
    completion_rate: 0,
  };
}

function normalizeSummary(
  raw: Record<string, unknown> | null | undefined,
  root: Record<string, unknown> | null | undefined = null,
): DashboardSummary {
  const summary = raw ?? {};
  const response = root ?? {};
  const totalTickets = toNumber(summary.totalTickets ?? response.totalTickets ?? summary.total_tasks ?? response.total_tasks);
  const notStartedTickets = toNumber(summary.notStartedTickets ?? response.notStartedTickets ?? summary.not_started ?? response.not_started);
  const ongoingTickets = toNumber(summary.ongoingTickets ?? response.ongoingTickets ?? summary.in_progress ?? response.in_progress);
  const closedTickets = toNumber(summary.closedTickets ?? response.closedTickets ?? summary.completed ?? response.completed);
  const openTickets = toNumber(summary.openTickets ?? response.openTickets ?? summary.pending ?? response.pending);
  // ticketRequests is intentionally separate from totalTickets; keep this KPI independent.
  const ticketRequests = toNumber(
    summary.ticketRequests ??
    response.ticketRequests ??
    summary.ticket_requests ??
    response.ticket_requests,
  );
  const newLeads = toNumber(summary.newLeads ?? response.newLeads ?? summary.new_leads ?? response.new_leads);
  const overdue = toNumber(summary.overdue ?? response.overdue);
  const completionRate = toNumber(summary.completion_rate ?? response.completion_rate);

  return {
    totalTickets,
    notStartedTickets,
    ongoingTickets,
    closedTickets,
    openTickets,
    ticketRequests,
    newLeads,
    total_tasks: totalTickets,
    in_progress: ongoingTickets,
    completed: closedTickets,
    pending: openTickets,
    overdue,
    active_tickets: openTickets,
    ticket_requests: ticketRequests,
    new_leads: newLeads,
    completion_rate: completionRate,
  };
}

function normalizeDashboardResponse(
  response: DashboardAnalyticsResponse | Record<string, any>,
): DashboardAnalyticsResponse {
  const rawSummary = (response as Record<string, any>).summary ?? response;
  const summary = normalizeSummary(
    rawSummary as Record<string, unknown> | null | undefined,
    response as Record<string, unknown> | null | undefined,
  );

  return {
    summary,
    workload_by_status: (response as DashboardAnalyticsResponse).workload_by_status ?? [],
    progress_flow: (response as DashboardAnalyticsResponse).progress_flow ?? [],
    service_package_kpis: (response as DashboardAnalyticsResponse).service_package_kpis ?? [],
    recent_activities: (response as DashboardAnalyticsResponse).recent_activities ?? [],
    reports: (response as DashboardAnalyticsResponse).reports ?? [],
    client_overview: (response as DashboardAnalyticsResponse).client_overview ?? [],
    scope: (response as DashboardAnalyticsResponse).scope,
  };
}

export function useDashboardAnalytics() {
  const auth = useAuthStore();
  // Start in loading mode so the first render never flashes empty dashboard content.
  const loading = ref(true);
  const clientsLoading = ref(false);
  const error = ref<string | null>(null);
  const lastUpdated = ref<string | null>(null);
  const dashboard = ref<DashboardAnalyticsResponse>(normalizeDashboardResponse({
    summary: createEmptySummary(),
    workload_by_status: [],
    progress_flow: [],
    service_package_kpis: [],
    recent_activities: [],
    reports: [],
    client_overview: [],
  }));
  const clients = ref<DashboardClientOption[]>([]);
  const filters = ref<DashboardFilters>({ ...DEFAULT_FILTERS });
  let ready = false;
  let refreshTimer: number | null = null;

  const role = computed<DashboardRole>(() => {
    if (auth.isSuperAdmin) return "super_admin";
    if (auth.userType === "client") return "client";
    if (auth.isProdOrMktgAdmin) return "admin";
    if (auth.accountType === "internal_user") return "member";
    return "guest";
  });

  const canFilterByClient = computed(() => role.value !== "client");

  const serviceOptions = computed(() => {
    const items = dashboard.value?.service_package_kpis ?? [];
    return Array.from(
      new Map(
        items
          .filter((item) => item.service_id !== null)
          .map((item) => [String(item.service_id), item.service_name || "Unassigned"]),
      ).entries(),
    ).map(([value, label]) => ({
      value,
      label,
    }));
  });

  const packageOptions = computed(() => {
    const items = dashboard.value?.service_package_kpis ?? [];
    return Array.from(
      new Map(
        items
          .filter((item) => item.package_id !== null)
          .map((item) => [String(item.package_id), item.package_name || "Unassigned"]),
      ).entries(),
    ).map(([value, label]) => ({
      value,
      label,
    }));
  });

  const reportArchive = computed<DashboardReportItem[]>(() => dashboard.value?.reports ?? []);

  function applyRoleDefaults() {
    if (role.value === "client" && auth.clientProfile?.client_id) {
      filters.value.client_id = String(auth.clientProfile.client_id);
      filters.value.date_from = getClientDashboardStartDate(
        auth.clientProfile.source_date_added,
        auth.clientProfile.created_at,
      );
      return;
    }

    filters.value.client_id = "all";
  }

  async function fetchClients() {
    if (!canFilterByClient.value) {
      clients.value = [];
      return;
    }

    clientsLoading.value = true;
    try {
      const clientStore = useClientStore();
      const list = await clientStore.fetchClients();
      clients.value = list.map((c) => ({
        id: Number(c.id),
        client_id: String(c.id),
        company_name: c.company_name || c.name,
        active_services_count: c.services?.length ?? 0,
      }));
    } catch {
      clients.value = [];
    } finally {
      clientsLoading.value = false;
    }
  }

  async function fetchDashboard(forceRefresh = false) {
    error.value = null;
    const params: Record<string, string | number> = {};
    const clientId = normalizeSelectValue(filters.value.client_id);

    if (canFilterByClient.value && clientId) {
      params.client_id = clientId;
    }
    if (filters.value.service_id !== null && Number.isFinite(filters.value.service_id)) {
      params.service_id = filters.value.service_id;
    }
    if (filters.value.package_id !== null && Number.isFinite(filters.value.package_id)) {
      params.package_id = filters.value.package_id;
    }
    if (filters.value.date_from) params.date_from = filters.value.date_from;
    if (filters.value.date_to) params.date_to = filters.value.date_to;

    const cacheKey = buildCacheKey("dashboard", role.value, clientId, filters.value.service_id, filters.value.package_id, filters.value.date_from, filters.value.date_to);
    const cached = useCachedResource<DashboardAnalyticsResponse>(cacheKey, CACHE_TTL.dashboard);

    const result = await cached.load(
      async () => {
        const { data } = await http.get<DashboardAnalyticsResponse>("/api/dashboard/analytics", {
          params,
        });
        return normalizeDashboardResponse(data as DashboardAnalyticsResponse);
      },
      { forceRefresh }
    );

    if (result) {
      dashboard.value = result;
      lastUpdated.value = new Date().toISOString();
    }
    loading.value = cached.loading.value;
  }

  async function refresh() {
    await fetchDashboard(true);
  }

  async function initialize() {
    applyRoleDefaults();
    await fetchClients();
    await fetchDashboard();
    ready = true;
  }

  async function createAnalyticsReport(request: DashboardReportRequest | DashboardFilters) {
    const payload = ("supporting_resources" in request
      ? request
      : {
        ...request,
        include_appendix: false,
        supporting_resources: [],
      }) as DashboardReportRequest;

    const formData = new FormData();
    formData.append("client_id", payload.client_id);
    if (payload.service_id !== null && payload.service_id !== undefined) {
      formData.append("service_id", String(payload.service_id));
    }
    if (payload.package_id !== null && payload.package_id !== undefined) {
      formData.append("package_id", String(payload.package_id));
    }
    if (payload.date_from) formData.append("date_from", payload.date_from);
    if (payload.date_to) formData.append("date_to", payload.date_to);
    formData.append("include_appendix", payload.include_appendix ? "1" : "0");

    (payload.supporting_resources ?? []).forEach((file) => {
      formData.append("supporting_resources[]", file);
    });

    const { data } = await httpMultipart.post("/api/reports/analytics/generate", formData);
    return data as {
      report_id: string;
      preview_url: string;
      download_url: string;
    };
  }

  async function downloadReport(report: DashboardReportItem | DashboardFilters) {
    const reportUrl = "pdf_url" in report && report.pdf_url && "report_id" in report && report.report_id
      ? report.pdf_url
      : null;

    if (reportUrl) {
      const { data } = await http.get(reportUrl, {
        responseType: "blob",
      });
      downloadBlob(data, "dmsuite-monthly-progress-report.pdf");
      return;
    }

    const payload = "filters" in report ? report.filters : report;
    const snapshot = await createAnalyticsReport({
      ...payload,
      include_appendix: false,
      supporting_resources: [],
    });
    const { data } = await http.get(snapshot.download_url, {
      responseType: "blob",
    });
    downloadBlob(data, "dmsuite-monthly-progress-report.pdf");
  }

  async function generateReport(request: DashboardReportRequest) {
    return createAnalyticsReport(request);
  }

  function downloadBlob(data: BlobPart, filename: string) {
    const blob = new Blob([data], { type: "application/pdf" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  watch(
    filters,
    () => {
      if (!ready) return;

      if (refreshTimer) {
        window.clearTimeout(refreshTimer);
      }

      refreshTimer = window.setTimeout(() => {
        void fetchDashboard();
      }, 250);
    },
    { deep: true },
  );

  onMounted(() => {
    void initialize();
  });

  return {
    role,
    loading,
    clientsLoading,
    error,
    dashboard,
    clients,
    filters,
    lastUpdated,
    serviceOptions,
    packageOptions,
    reportArchive,
    canFilterByClient,
    refresh,
    fetchClients,
    fetchDashboard,
    downloadReport,
    generateReport,
  };
}
